import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { KeyRound, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChangePasswordModal() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user?.require_password_change) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/api/auth/profile`, { new_password: newPassword });
      toast.success("Password updated — welcome to BridgeHub!");
      await checkAuth();
      navigate("/app/webhooks");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white border border-[#0A1128]/15 rounded-sm w-full max-w-sm shadow-2xl">
        <div className="p-6 border-b border-[#0A1128]/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#0A1128]">Set Your Password</h2>
              <p className="text-xs text-[#0A1128]/50 mt-0.5">Your account requires a new password before continuing.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0A1128]/60 mb-1.5">
              New Password
            </label>
            <div className="relative">
              <input
                data-testid="first-login-new-password"
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Min. 8 characters"
                className="w-full bg-white border border-[#0A1128]/20 rounded-sm px-3 py-2 pr-9 text-sm text-[#0A1128] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 transition-all"
              />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#0A1128]/40 hover:text-[#0A1128]/70">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0A1128]/60 mb-1.5">
              Confirm Password
            </label>
            <div className="relative">
              <input
                data-testid="first-login-confirm-password"
                type={showConfirm ? "text" : "password"}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                placeholder="Re-enter your password"
                className="w-full bg-white border border-[#0A1128]/20 rounded-sm px-3 py-2 pr-9 text-sm text-[#0A1128] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/20 transition-all"
              />
              <button type="button" onClick={() => setShowConfirm(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#0A1128]/40 hover:text-[#0A1128]/70">
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            data-testid="first-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A1128] text-[#F3F0EA] rounded-sm py-2.5 text-sm font-medium hover:bg-[#16234D] disabled:opacity-50 transition-colors mt-2"
          >
            {loading ? "Updating..." : "Set Password & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
